// src/stores/pills.js
import { defineStore } from 'pinia'
import { getFromLocalStorage, saveToLocalStorage } from 'src/functions/function-local-storage'
import { fbDatabase } from 'src/boot/firebase'
import { setDbChild, updateDbChild } from 'src/functions/function-fb-crud'
import { useProgressStore } from 'src/stores/store-progress'
import { useUserStore } from './store-user'

export const usePillsStore = defineStore('pills', {
  state: () => ({
    pills: {},
    publicPacks: {},
    levelRange: {
      min: 1,
      max: 10,
    },
    selectedPackId: null,
    search: '',
    sort: 'a',
    tag: '',
    pillsDownloaded: false,
    publicPacksDownloaded: false,
    pillsLoading: null, // { done, total } while fetchPillsData downloads pills missing from the cache
  }),

  getters: {
    userId() {
      return useUserStore().uid
    },
    userPillsList(state) {
      // Get the list of pill IDs the user actually owns
      const progressStore = useProgressStore()
      const userInventory = progressStore.userPills || {}
      const initialList = state.pills
      const finalList = {}

      Object.keys(initialList).forEach((pillId) => {
        const pill = initialList[pillId]
        // Only include if not archived and user owns it
        if (!pill.archived && userInventory[pillId]) {
          finalList[pillId] = pill
        }
      })

      return finalList
    },

    pillsByPackIdList() {
      return (packId) => {
        const initialList = this.userPillsList
        const finalList = {}

        Object.keys(initialList).forEach((pillId) => {
          const pill = initialList[pillId]
          if (pill.packId === packId) {
            finalList[pillId] = pill
          }
        })

        return finalList
      }
    },
  },

  actions: {
    // Many pills at once (boot, a pack's new pills): the store and the cache are written once per chunk,
    // not once per pill, so the app re-renders a few times instead of once per pill (first boot froze the phone).
    async fetchPillsData(pillIds, chunkSize = 50) {
      const cache = getFromLocalStorage('pills_data_v2') || {}
      const cached = {}
      const missing = []
      pillIds.forEach((id) => {
        if (this.pills[id]) return
        if (cache[id]) cached[id] = cache[id]
        else missing.push(id)
      })
      if (Object.keys(cached).length) Object.assign(this.pills, cached)
      if (!missing.length) return

      // shared by calls running at the same time: totals add up, cleared when every call is done
      this.pillsLoading = { done: this.pillsLoading?.done || 0, total: (this.pillsLoading?.total || 0) + missing.length }
      let counted = 0
      try {
        for (let i = 0; i < missing.length; i += chunkSize) {
          const chunk = missing.slice(i, i + chunkSize)
          const results = await Promise.all(
            chunk.map((id) =>
              fbDatabase
                .ref(`pills-data/${id}`)
                .once('value')
                .then((snapshot) => [id, snapshot.val()])
                .catch(() => [id, null]),
            ),
          )
          const fetched = Object.fromEntries(results.filter(([, pill]) => pill))
          Object.assign(this.pills, fetched)
          // read the cache again: single fetchPillData calls may have written to it meanwhile
          saveToLocalStorage('pills_data_v2', { ...(getFromLocalStorage('pills_data_v2') || {}), ...fetched })
          if (this.pillsLoading) this.pillsLoading.done += chunk.length
          counted += chunk.length
        }
      } finally {
        // on an error, count the rest as done so the progress hint never stays on screen
        if (this.pillsLoading) {
          this.pillsLoading.done += missing.length - counted
          if (this.pillsLoading.done >= this.pillsLoading.total) this.pillsLoading = null
        }
      }
    },

    async fetchPillData(pillId) {
      // Try local cache first
      const localPill = getFromLocalStorage(`pills_data_v2/${pillId}`)
      if (localPill) {
        this.pills[pillId] = localPill
        return localPill
      }
      // Otherwise load from RTDB
      try {
        const snapshot = await fbDatabase.ref(`pills-data/${pillId}`).once('value')
        if (snapshot.exists()) {
          const pill = snapshot.val()
          this.pills[pillId] = pill
          saveToLocalStorage(`pills_data_v2/${pillId}`, pill)
          return pill
        } else {
        }
      } catch (error) {
      }
    },

    async fetchUserPillData(pillId) {
      const localPill = getFromLocalStorage(`pills_data_v2/${pillId}`)
      if (localPill) {
        this.pills[pillId] = localPill
      } else {
        try {
          const snapshot = await fbDatabase.ref(`user-pills-data/${this.userId}/${pillId}`).once('value')
          if (snapshot.exists()) {
            const pill = snapshot.val()
            this.pills[pillId] = pill
            // Optionally cache it locally:
            saveToLocalStorage(`pills_data_v2/${pillId}`, pill)
          } else {
          }
        } catch (error) {
        }
      }
    },

    async createPill({ data, packId }) {
      // Generate push key
      const pushKey = fbDatabase.ref(`user-pills-data/${this.userId}`).push().getKey()
      data.id = pushKey
      data.by = this.userId // Store author in pill data for easy access
      if (!packId) {
        packId = 'default'
      }
      data.packId = packId
      // Convert any array fields into CSV
      Object.keys(data).forEach((k) => {
        if (Array.isArray(data[k])) {
          data[k] = data[k].toString()
        }
      })

      // Write pill data under user-pills-data/userId
      setDbChild({
        path: `user-pills-data/${this.userId}/${pushKey}`,
        data: data,
        message: `Pill created with id: ${pushKey}`,
      })

      // Initialize stats in pills-stats (unified location)
      setDbChild({
        path: `pills-stats/${pushKey}`,
        data: {
          author: this.userId,
          createdAt: Date.now(),
          collectedBy: {
            [this.userId]: Date.now()
          },
          counts: {
            collected: 1
          }
        }
      })

      // If packId set, add a reference under user-progress/packs
      if (packId) {
        setDbChild({
          path: `user-progress/${this.userId}/packs/${packId}/pills/${pushKey}`,
          data: true,
        })
      }

      // Add to user inventory as CREATED pill (not collected)
      setDbChild({
        path: `user-progress/${this.userId}/inventory/pillCreated/${pushKey}`,
        data: Date.now()
      })

      // Cache locally and update state
      saveToLocalStorage(`pills_data_v2/${pushKey}`, data)
      this.pills[pushKey] = data

      return { pill: data }
    },

    /**
     * Share a pill with one or more users
     */
    async sharePill({ pillId, recipientUids = [] }) {
      const pill = this.pills[pillId]

      if (!pill) {
        console.error('Pill not found:', pillId)
        return { error: 'Pill not found' }
      }

      const timestamp = Date.now()

      // Add to sharedWith in pills-stats
      const updates = {}
      recipientUids.forEach(uid => {
        updates[`pills-stats/${pillId}/sharedWith/${uid}`] = timestamp
      })

      await fbDatabase.ref().update(updates)

      // Send notifications to recipients (flat structure at user-notifications/{uid}/{pushKey})
      const notificationPromises = recipientUids.map(async uid => {
        const notificationRef = fbDatabase.ref(`user-notifications/${uid}`).push()
        return notificationRef.set({
          type: 'pill_shared',
          pillId,
          sharedBy: this.userId,
          timestamp,
          date: timestamp,
          status: 'pending'
        })
      })

      await Promise.all(notificationPromises)

      return { success: true, sharedCount: recipientUids.length }
    },

    /**
     * Accept a shared pill
     */
    async acceptSharedPill({ pillId, authorUid }) {

      // Check if pill is shared with me
      const sharedSnapshot = await fbDatabase.ref(`pills-stats/${pillId}/sharedWith/${this.userId}`).once('value')

      if (!sharedSnapshot.exists()) {
        throw new Error('Pill not shared with you')
      }

      // Collect pill (adds to collectedBy)
      await setDbChild({
        path: `pills-stats/${pillId}/collectedBy/${this.userId}`,
        data: Date.now()
      })

      // Update count
      const countRef = fbDatabase.ref(`pills-stats/${pillId}/counts/collected`)
      await countRef.transaction(count => (count || 0) + 1)

      // Fetch pill data to get packId
      const pillSnapshot = await fbDatabase.ref(`user-pills-data/${authorUid}/${pillId}`).once('value')
      const pill = pillSnapshot.val()

      if (pill) {
        // Add to my pills cache
        this.pills[pillId] = pill

        // Collect pill to inventory (triggers existing flow)
        const progressStore = useProgressStore()
        progressStore.collectPill({
          pillId,
          packId: pill.packId || 'shared_pills',
          source: 'shared_pill_accepted'
        })
      }

      // Update notification status
      await updateDbChild({
        path: `user-notifications/${this.userId}/pill-shares/${pillId}`,
        data: { status: 'accepted' }
      })

      return { success: true }
    },

    /**
     * Fetch pills shared with current user
     */
    async fetchSharedWithMe() {
      // Query pills-stats where sharedWith includes my uid
      const snapshot = await fbDatabase.ref('pills-stats')
        .orderByChild(`sharedWith/${this.userId}`)
        .startAt(1)
        .once('value')

      const sharedPills = []
      if (snapshot.exists()) {
        snapshot.forEach(statSnap => {
          const pillId = statSnap.key
          const stats = statSnap.val()

          // Only include user pills (have author field)
          if (!stats.author) return

          // Check if I already collected it
          const alreadyCollected = stats.collectedBy?.[this.userId]

          if (!alreadyCollected) {
            sharedPills.push({
              pillId,
              sharedBy: stats.author,
              sharedAt: stats.sharedWith[this.userId],
              stats
            })
          }
        })
      }

      return sharedPills
    },

    /**
     * Remove sharing (author revokes)
     */
    async unsharePill({ pillId, recipientUids = [] }) {
      const updates = {}

      recipientUids.forEach(uid => {
        // Remove from pills-stats sharedWith
        updates[`pills-stats/${pillId}/sharedWith/${uid}`] = null

        // Remove notification
        updates[`user-notifications/${uid}/pill-shares/${pillId}`] = null
      })

      await fbDatabase.ref().update(updates)

      return { success: true }
    },
  },
})
